import React, { useEffect, useState } from "react"
import {
    Box,
    List,
    ListItemButton,
    ListItemText,
    Collapse,
    Typography,
    IconButton,
    Drawer,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import { ReactComponent as HamburguerMenu } from "src/v2/assets/icons/hamburguer-menu-icon.svg"
import { ReactComponent as CloseMenuIcon } from "src/v2/assets/icons/close-square-icon.svg"
import theme from "../../theme/theme"
import { Logo } from "../Logo/logo"
import { MENU_ITEMS } from "./constants/menu-items.const"
import { useLocation, useNavigate } from "react-router-dom"
import { TokenService } from "../../../services/token-service"

export const HEIGHT_TOP_MENU = "64px"

const Sidebar = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const flattenMenu = (items) =>
        items.flatMap((item) => [
            ...(item.route ? [{ route: item.route, label: item.label }] : []),
            ...(item.children ? flattenMenu(item.children) : []),
        ])

    const ROUTE_LABEL_MAP = Object.fromEntries(
        flattenMenu(MENU_ITEMS).map(({ route, label }) => [route, label])
    )
    // mínimo necessário
    const getActiveLabelFromPath = (pathname) => {
        const match = Object.keys(ROUTE_LABEL_MAP).find((route) =>
            pathname.startsWith(route)
        )
        return match ? ROUTE_LABEL_MAP[match] : undefined
    }

    const [open, setOpen] = useState({})
    const [active, setActive] = useState(
        getActiveLabelFromPath(location.pathname)
    )

    const [isVisibleMenuSmallScreen, setVisibleMenuSmallScreen] =
        useState(false)

    const muiTheme = useTheme()
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("lg"))

    const tokenService = new TokenService()
    const isAdminUser = tokenService.verifyUserIsAdmin()

    const hasAccessToMenu = (item) =>
        !item.isAdmin &&
        (item.id == null || tokenService.verifyPermission(item.id))

    const visibleMenuItem = isAdminUser
        ? MENU_ITEMS
        : MENU_ITEMS.filter(hasAccessToMenu)

    const toggleIsVisibleMenuSmallScreen = () => {
        setVisibleMenuSmallScreen((prev) => !prev)
    }

    const toggle = (label) => setOpen((prev) => ({ [label]: !prev[label] }))

    const handleClick = (item) => {
        if (item.children) {
            toggle(item.label)
        } else {
            setActive(item.label)

            if (isMobile) {
                setVisibleMenuSmallScreen(false)
            }

            if (item.action === "logout") {
                localStorage.clear()
                window.location.href = "/login"
            } else if (item.route) {
                navigate(item.route)
            }
        }
    }

    const HEIGHT_TOP_MENU = "64px"

    useEffect(() => {
        setActive(getActiveLabelFromPath(location.pathname))
    }, [location.pathname])

    useEffect(() => {
        const parentsToOpen = {}
        MENU_ITEMS.forEach((item) => {
            if (item.children?.some((child) => child.label === active)) {
                parentsToOpen[item.label] = true
            }
        })
        setOpen((prev) => ({ ...prev, ...parentsToOpen }))
    }, [active])

    if (isMobile) {
        return (
            <>
                <Box
                    sx={{
                        width: "100%",
                        // maxWidth: { xxxs: "calc(100vw - 10px)" },
                        height: HEIGHT_TOP_MENU,
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.common.white,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        position: "fixed",
                        top: 0,
                        left: 0,
                        zIndex: 1300,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                >
                    <Logo />
                    <IconButton
                        sx={{
                            color: "common.white",
                            "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.1)",
                            },
                        }}
                        onClick={toggleIsVisibleMenuSmallScreen}
                    >
                        {isVisibleMenuSmallScreen ? (
                            <CloseMenuIcon
                                style={{ color: theme.palette.icon.main }}
                            />
                        ) : (
                            <HamburguerMenu
                                style={{ color: theme.palette.icon.main }}
                            />
                        )}
                    </IconButton>
                </Box>

                <Drawer
                    anchor="top"
                    open={isVisibleMenuSmallScreen}
                    onClose={toggleIsVisibleMenuSmallScreen}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        "& .MuiDrawer-paper": {
                            mt: 7,
                            width: "100%",
                            height: "fit-content",
                            maxHeight: `calc(100vh - ${HEIGHT_TOP_MENU})`,
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.common.white,
                            zIndex: 1200,
                            display: "flex",
                            flexDirection: "column",
                        },
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            "&::-webkit-scrollbar": {
                                width: "16px",
                                background: "transparent",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#D9D9D9",
                                opacity: "40%",
                                borderRadius: "8px",
                                border: `5px solid transparent`,
                                backgroundClip: "padding-box",
                                marginBlockStart: "16px",
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "transparent",
                                marginBlockStart: "4px",
                            },
                        }}
                    >
                        <List>
                            {visibleMenuItem.map((item) => (
                                <React.Fragment key={item.label}>
                                    <ListItemButton
                                        sx={{
                                            px: 3,
                                            bgcolor:
                                                active === item.label
                                                    ? "#2c3255"
                                                    : "transparent",
                                            "&:hover": { bgcolor: "#14192B" },
                                        }}
                                        onClick={() => handleClick(item)}
                                    >
                                        <ListItemText>
                                            <Typography
                                                data-testid={item.label}
                                                variant="contraHeading"
                                                color="common.white"
                                                sx={{
                                                    opacity:
                                                        active === item.label
                                                            ? 1
                                                            : 0.6,
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                        </ListItemText>
                                        {item.children && (
                                            <>
                                                {open[item.label] ? (
                                                    <Typography
                                                        variant="body2"
                                                        color="icon.white60"
                                                    >
                                                        -
                                                    </Typography>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="icon.white60"
                                                    >
                                                        +
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </ListItemButton>
                                    {item.children && (
                                        <Collapse
                                            in={open[item.label]}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <List
                                                component="div"
                                                disablePadding
                                            >
                                                {(isAdminUser
                                                    ? item.children
                                                    : item.children.filter(
                                                          hasAccessToMenu
                                                      )
                                                ).map((sub) => (
                                                    <ListItemButton
                                                        key={sub.label}
                                                        sx={{
                                                            pl: 4,
                                                            "&:hover": {
                                                                bgcolor:
                                                                    "#14192B",
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            handleClick(sub)
                                                        }
                                                    >
                                                        <ListItemText>
                                                            <Typography variant="textBody">
                                                                {sub.label}
                                                            </Typography>
                                                        </ListItemText>
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Collapse>
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                </Drawer>
            </>
        )
    }

    return (
        <Box
            sx={{
                width: 209,
                height: "100vh",
                bgcolor: theme.palette.background.paper,
                color: theme.palette.common.white,
                display: "flex",
                flexDirection: "column",
                position: "fixed",
            }}
        >
            <Box sx={{ p: 2, placeSelf: "center" }}>
                <Logo />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                        width: "16px",
                        background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#D9D9D9",
                        opacity: "40%",
                        borderRadius: "8px",
                        border: `5px solid transparent`,
                        backgroundClip: "padding-box",
                        marginBlockStart: "16px",
                    },
                    "&::-webkit-scrollbar-track": {
                        background: "transparent",
                        marginBlockStart: "4px",
                    },
                }}
            >
                <List>
                    {visibleMenuItem.map((item) => (
                        <React.Fragment key={item.label}>
                            <ListItemButton
                                data-testid={item.label}
                                sx={{
                                    pl: 2,
                                    bgcolor:
                                        active === item.label
                                            ? "#2c3255"
                                            : "transparent",
                                    "&:hover": { bgcolor: "#14192B" },
                                }}
                                onClick={() => handleClick(item)}
                            >
                                <ListItemText>
                                    <Typography
                                        variant="contraHeading"
                                        color="common.white"
                                        sx={{
                                            opacity:
                                                active === item.label ? 1 : 0.6,
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                </ListItemText>
                                {item.children && (
                                    <>
                                        {open[item.label] ? (
                                            <Typography
                                                variant="body2"
                                                color="icon.white60"
                                            >
                                                -
                                            </Typography>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="icon.white60"
                                            >
                                                +
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </ListItemButton>
                            {item.children && (
                                <Collapse
                                    in={open[item.label]}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <List component="div" disablePadding>
                                        {(isAdminUser
                                            ? item.children
                                            : item.children.filter(
                                                  hasAccessToMenu
                                              )
                                        ).map((sub) => (
                                            <ListItemButton
                                                data-testid={sub.label}
                                                key={sub.label}
                                                sx={{
                                                    pl: 4,
                                                    bgcolor:
                                                        active === sub.label
                                                            ? "#2c3255"
                                                            : "transparent",

                                                    "&:hover": {
                                                        bgcolor: "#14192B",
                                                    },
                                                }}
                                                onClick={() => handleClick(sub)}
                                            >
                                                <ListItemText>
                                                    <Typography variant="textBody">
                                                        {sub.label}
                                                    </Typography>
                                                </ListItemText>
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Box>
        </Box>
    )
}

export default Sidebar
